import { For, Show, createSignal, onCleanup, type JSX } from "solid-js";
import { useI18n } from "../i18n/provider.tsx";
import styles from "./Table.module.scss";

export type Column<Row> = {
  key: string;
  header: string;
  align?: "start" | "end";
  cell: (row: Row) => JSX.Element;
};

export type TableProps<Row> = {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** The element id of a row, for a screen that has to scroll one back into view. */
  rowId?: (row: Row) => string;
  caption?: string;
  emptyLabel?: string;
};

/** No column may be dragged narrower than this; below it a header is unreadable. */
const minWidth = 72;

/** One arrow key press, and the same press with shift held. */
const nudge = 16;
const leap = 64;

/** Which edges of the scroll port still hide content. */
type Edges = { start: boolean; end: boolean; top: boolean };

/**
 * The one table in the app. Every admin screen composes columns instead of
 * hand-rolling markup, which keeps the table-heavy UI consistent.
 *
 * Three things the screens rely on and none of them implement themselves:
 *
 *  * The header stays put. The frame caps its own height, so a long list
 *    scrolls inside it and the column names never leave the reader.
 *  * A cut-off edge says so. Where the content continues, it fades into a shade
 *    below the page ground; a border would read as the end of the table.
 *  * Columns are the reader's. A consultant comparing long product names
 *    against short scores drags the split where they need it, by pointer or by
 *    arrow key, and a double click hands the layout back to the browser.
 */
export const Table = <Row,>(props: TableProps<Row>) => {
  const i18n = useI18n();

  let scroll: HTMLDivElement | undefined;
  const heads = new Map<string, HTMLTableCellElement>();

  const [edges, setEdges] = createSignal<Edges>({ start: false, end: false, top: false });
  /** Empty until the reader takes the columns over; then every column but the last. */
  const [widths, setWidths] = createSignal<Record<string, number>>({});
  const [dragging, setDragging] = createSignal<string | undefined>();

  const sized = () => Object.keys(widths()).length > 0;

  /*
   * Both scroll axes and every resize feed the same read, because the edges
   * depend on content width, frame width and scroll offset together. A single
   * pixel of slack keeps a fractional layout from flickering the fade on.
   */
  const sync = () => {
    const port = scroll;
    if (port === undefined) return;
    const room = port.scrollWidth - port.clientWidth;
    const left = Math.abs(port.scrollLeft);
    setEdges({ start: left > 1, end: left < room - 1, top: port.scrollTop > 1 });
  };

  const observe = (element: HTMLDivElement) => {
    scroll = element;
    const watcher = new ResizeObserver(sync);
    watcher.observe(element);
    for (const child of element.children) watcher.observe(child);
    onCleanup(() => watcher.disconnect());
    queueMicrotask(sync);
  };

  /**
   * The widths as they stand right now, measured once on the first drag.
   *
   * The last column is left out on purpose: it absorbs whatever the others give
   * up or take, so the table keeps filling its frame instead of ending in a
   * ragged gap the first time somebody narrows a column.
   */
  const freeze = (): Record<string, number> => {
    const current = widths();
    if (Object.keys(current).length > 0) return current;
    const measured: Record<string, number> = {};
    for (const column of props.columns.slice(0, -1)) {
      const head = heads.get(column.key);
      if (head !== undefined) measured[column.key] = head.getBoundingClientRect().width;
    }
    setWidths(measured);
    return measured;
  };

  const resize = (key: string, base: Record<string, number>, width: number) => {
    setWidths({ ...base, [key]: Math.max(minWidth, Math.round(width)) });
    queueMicrotask(sync);
  };

  const drag = (key: string, event: PointerEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const base = freeze();
    const from = event.clientX;
    const start = base[key] ?? minWidth;
    setDragging(key);

    const move = (step: PointerEvent) => resize(key, base, start + (step.clientX - from));
    const drop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", drop);
      window.removeEventListener("pointercancel", drop);
      document.body.style.removeProperty("cursor");
      setDragging(undefined);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", drop);
    window.addEventListener("pointercancel", drop);
    // The pointer leaves the handle within the first few pixels of a drag.
    document.body.style.setProperty("cursor", "col-resize");
  };

  const key = (column: string, event: KeyboardEvent) => {
    const step = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const base = freeze();
    resize(column, base, (base[column] ?? minWidth) + step * (event.shiftKey ? leap : nudge));
  };

  /** Back to the browser's own layout, which is what a reader means by "undo this". */
  const release = () => {
    setWidths({});
    queueMicrotask(sync);
  };

  const widthOf = (column: string): string | undefined => {
    const width = widths()[column];
    return width === undefined ? undefined : `${width}px`;
  };

  return (
    <div
      class={styles.frame}
      data-start={edges().start ? "" : undefined}
      data-end={edges().end ? "" : undefined}
      data-lifted={edges().top ? "" : undefined}
    >
      <div class={styles.scroll} ref={observe} onScroll={sync}>
        <table
          class={`${styles.table} ${sized() ? styles.sized : ""}`}
          data-dragging={dragging() === undefined ? undefined : ""}
        >
          <Show when={props.caption !== undefined}>
            <caption class={styles.caption}>{props.caption}</caption>
          </Show>
          <colgroup>
            <For each={props.columns}>
              {(column) => <col style={{ width: widthOf(column.key) }} />}
            </For>
          </colgroup>
          <thead>
            <tr>
              <For each={props.columns}>
                {(column, index) => (
                  <th
                    class={`${styles.head} ${column.align === "end" ? styles.end : ""}`}
                    ref={(element) => heads.set(column.key, element)}
                    scope="col"
                  >
                    {column.header}
                    <Show when={index() < props.columns.length - 1}>
                      <span
                        class={styles.grip}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`${i18n.t().common.resizeColumn}: ${column.header}`}
                        aria-valuenow={
                          widths()[column.key] === undefined
                            ? undefined
                            : Math.round(widths()[column.key] ?? 0)
                        }
                        aria-valuemin={widths()[column.key] === undefined ? undefined : minWidth}
                        tabindex="0"
                        title={i18n.t().common.resizeColumnHint}
                        data-active={dragging() === column.key ? "" : undefined}
                        onPointerDown={(event) => drag(column.key, event)}
                        onDblClick={release}
                        onKeyDown={(event) => key(column.key, event)}
                      />
                    </Show>
                  </th>
                )}
              </For>
            </tr>
          </thead>
          <tbody>
            <Show
              when={props.rows.length > 0}
              fallback={
                <tr>
                  <td class={styles.empty} colSpan={props.columns.length}>
                    {props.emptyLabel ?? ""}
                  </td>
                </tr>
              }
            >
              <For each={props.rows}>
                {(row) => (
                  <tr class={styles.row} id={props.rowId?.(row)}>
                    <For each={props.columns}>
                      {(column) => (
                        <td class={`${styles.cell} ${column.align === "end" ? styles.end : ""}`}>
                          {column.cell(row)}
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>

      {/*
        The two edges are painted over the scroll port rather than inside it, so
        they stay at the frame's edge while the content moves underneath. They
        take no pointer, because a cell under the fade is still a cell.
      */}
      <span class={`${styles.fade} ${styles.fadeStart}`} aria-hidden="true" />
      <span class={`${styles.fade} ${styles.fadeEnd}`} aria-hidden="true" />
    </div>
  );
};
